import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import '../../styles/admin/AddProjectGroup.css'; // Import the shared CSS
import ProjectCategories from '../../constants/projectCategories';
import { uploadToCloudinary } from '../../utils/cloudinary';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';

// Removed BACKEND_URL, use apiFetch instead

export default function EditProjectGroup() {
  // const navigate = useNavigate(); // Removed as unused
  const [allGroups, setAllGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(''); // Removed setError as unused
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    image: '',
    sections: [],
    members: [],
  });
  const [membersList, setMembersList] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false); // New state for image upload status

  // Fetch all project groups for search
  useEffect(() => {
    apiFetch('/api/project-groups')
      .then(data => {
        setAllGroups(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setAllGroups([]);
        setLoading(false);
      });
  }, []);

  // Fetch all members for assignment
  useEffect(() => {
    apiFetch('/api/members')
      .then(data => setMembersList(Array.isArray(data.members) ? data.members : []))
      .catch(() => setMembersList([]));
  }, []);

  // Filter members by search
  useEffect(() => {
    if (!memberSearch) {
      setFilteredMembers(membersList);
    } else {
      setFilteredMembers(
        membersList.filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
      );
    }
  }, [memberSearch, membersList]);

  // When a group is selected, set form state
  const handleSelect = (name) => {
    const selected = allGroups.find(g => g.name === name);
    if (selected) {
      setGroup(selected);
      setForm({
        name: selected.name || '',
        description: selected.description || '',
        category: selected.category || '',
        image: selected.image || '',
        sections: selected.sections || [],
        members: selected.members
          ? selected.members.map(m => m._id || m)
          : [],
      });
      // Set selectedMembers as objects
      if (selected.members && Array.isArray(selected.members)) {
        setSelectedMembers(
          selected.members.map(m => {
            if (typeof m === 'object' && m._id) return m;
            return membersList.find(mem => mem._id === m) || { _id: m, name: m };
          })
        );
      } else {
        setSelectedMembers([]);
      }
      setMessage('');
      setSearch(selected.name); // Keep selected name in search bar
    }
  };

  // Handle form changes
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  // Handle section changes
  const handleSectionChange = (idx, field, value) => {
    setForm(f => {
      const sections = [...f.sections];
      sections[idx][field] = value;
      return { ...f, sections };
    });
  };

  // Add/remove section
  const addSection = type => {
    setForm(f => ({ ...f, sections: [...f.sections, { type, content: '' }] }));
  };
  const removeSection = idx => {
    setForm(f => {
      const sections = [...f.sections];
      sections.splice(idx, 1);
      return { ...f, sections };
    });
  };

  // Add member to selectedMembers
  const handleSelectMember = (member) => {
    if (!selectedMembers.some(m => m._id === member._id)) {
      setSelectedMembers([...selectedMembers, member]);
      setForm(f => ({ ...f, members: [...f.members, member._id] }));
    }
  };

  // Remove member from selectedMembers
  const handleRemoveMember = (id) => {
    setSelectedMembers(selectedMembers.filter(m => m._id !== id));
    setForm(f => ({ ...f, members: f.members.filter(mid => mid !== id) }));
  };

  // Handle image upload for main group image
  const handleImageUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const previewUrl = URL.createObjectURL(file);
    setForm(f => ({ ...f, image: previewUrl })); // Show preview immediately
    try {
      const url = await uploadToCloudinary(file);
      if (url) {
        setForm(f => ({ ...f, image: url }));
        setMessage('');
      } else {
        setMessage('Image upload failed: Invalid URL returned.');
        setForm(f => ({ ...f, image: previewUrl })); // Revert to preview if upload fails
      }
    } catch (err) {
      setMessage('Image upload failed.');
      setForm(f => ({ ...f, image: previewUrl })); // Revert to preview if upload fails
    } finally {
      setImageUploading(false);
    }
  };

  // Handle section image upload
  const handleSectionImageUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageUploading(true);
    const previewUrl = URL.createObjectURL(file);
    handleSectionChange(idx, 'content', previewUrl); // Show preview immediately
    try {
      const url = await uploadToCloudinary(file);
      if (url) {
        handleSectionChange(idx, 'content', url);
        setMessage('');
      } else {
        setMessage('Section image upload failed: Invalid URL returned.');
        handleSectionChange(idx, 'content', previewUrl); // Revert to preview if upload fails
      }
    } catch {
      setMessage('Section image upload failed.');
      handleSectionChange(idx, 'content', previewUrl); // Revert to preview if upload fails
    } finally {
      setImageUploading(false);
    }
  };

  // Submit form
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    // Validation for image uploads
    if (imageUploading) {
      setMessage('Please wait for all images to finish uploading before saving.');
      setSaving(false);
      return;
    }

    // Ensure all section images have valid URLs
    const invalidSectionImage = form.sections.some(s => s.type === 'image' && (!s.content || !s.content.startsWith('http')));
    if (invalidSectionImage) {
      setMessage('Some section images are still uploading or have invalid URLs. Please wait or re-upload.');
      setSaving(false);
      return;
    }

    try {
      if (!group || !group._id) {
        throw new Error("Group ID is missing for update.");
      }
      const data = await apiFetch(`/api/project-groups/${group._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (data && data.success) {
        setMessage('Project group updated successfully!');
        setAllGroups(allGroups.map(g => (g._id === group._id ? { ...group, ...form, _id: group._id } : g)));
        setGroup(prevGroup => ({ ...prevGroup, ...form })); // Update selected group as well
      } else {
        setMessage(data && data.message ? data.message : 'Update failed.');
      }
    } catch (err) {
      setMessage('Update failed: ' + err.message);
    }
    setSaving(false);
  };

  if (loading) return <div className="admin-add-page">Loading...</div>;
  if (error) return <div className="admin-add-page admin-error">{error}</div>;

  // Search UI and edit form in-place
  const allNames = allGroups.map(g => g.name);
  const results = search ? allGroups.filter(g => g.name.toLowerCase().includes(search.toLowerCase())) : [];

  return (
    <ProtectedAdminRoute>
      <div className="admin-add-page add-project-group-page"> {/* Use admin-add-page and add-project-group-page */}
        <div className="admin-add-header"> {/* Header styling from AddProjectGroup */}
          <h2 className="add-project-header">Edit Project Group</h2>
          <p className="admin-add-subtitle">Search for a project group to edit its details</p>
        </div>
        <div className="admin-add-form" style={{maxWidth: '800px', margin: '20px auto'}}> {/* Use admin-add-form for the container */}
          <div className="form-group">
            <label htmlFor="search">Search Project Group</label>
            <input
              type="text"
              id="search"
              placeholder="Search project group by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              list="group-names"
              autoComplete="off"
              className="form-control"
            />
            <datalist id="group-names">
              {allNames.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>

          {search && results.some(g => g.name.toLowerCase() === search.toLowerCase()) && !group && (
            <button
              className="action-btn primary"
              style={{ marginBottom: '1rem' }}
              onClick={() => handleSelect(search)}
              type="button"
            >
              Edit "{search}"
            </button>
          )}

          {search && !group && results.length > 0 && !results.some(g => g.name.toLowerCase() === search.toLowerCase()) && (
            <div className="member-search-results" style={{marginBottom:'1rem', border: '1px solid #ddd', borderRadius: '8px', padding: '10px'}}>
              {results.map(g => (
                <div
                  key={g._id}
                  className="member-search-item"
                  onClick={() => handleSelect(g.name)}
                >
                  <strong>{g.name}</strong>
                  {g.category && <span style={{marginLeft:8, color:'#888', fontSize:'0.95em'}}>{g.category}</span>}
                  <div style={{fontSize:'0.95em', color:'#666'}}>{g.description}</div>
                </div>
              ))}
            </div>
          )}

          {group && (
            <form className="admin-add-form" onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  placeholder="Group Name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  required
                  className="form-control"
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="">Select category (optional)</option>
                  {ProjectCategories.OPTIONS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Group Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="form-control"
                  disabled={imageUploading}
                />
                {imageUploading && <div style={{color:'#888',marginTop:4}}>Uploading...</div>}
                {form.image && <img src={form.image} alt="Group" style={{maxWidth:180, borderRadius:8, marginTop:8}} />}
              </div>
              <div className="form-group">
                <label>Content Sections</label>
                {form.sections.map((section, idx) => (
                  <div key={idx} className="section-block">
                    <select
                      value={section.type}
                      onChange={e => handleSectionChange(idx, 'type', e.target.value)}
                      className="form-control"
                    >
                      <option value="paragraph">Paragraph</option>
                      <option value="image">Image</option>
                    </select>
                    {section.type === 'paragraph' ? (
                      <textarea
                        value={section.content}
                        onChange={e => handleSectionChange(idx, 'content', e.target.value)}
                        className="form-control"
                        placeholder="Paragraph text"
                        rows={3}
                      />
                    ) : (
                      <div>
                        <input
                          type="text"
                          value={section.content}
                          onChange={e => handleSectionChange(idx, 'content', e.target.value)}
                          placeholder="Image URL or upload below"
                          className="form-control"
                          style={{marginBottom:6}}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => handleSectionImageUpload(idx, e)}
                          className="form-control"
                          disabled={imageUploading}
                        />
                        {imageUploading && <div style={{color:'#888',marginTop:4}}>Uploading...</div>}
                        {section.content && typeof section.content === 'string' && section.content.startsWith('http') && (
                          <img src={section.content} alt="Section" style={{maxWidth:140, marginTop:6, borderRadius:6}} />
                        )}
                      </div>
                    )}
                    <button type="button" onClick={() => removeSection(idx)} className="action-btn secondary" style={{marginTop: 4}}>Remove</button>
                  </div>
                ))}
                <div style={{marginTop:8}}>
                  <button type="button" onClick={() => addSection('paragraph')} className="action-btn primary" style={{marginRight: 8}}>Add Paragraph</button>
                  <button type="button" onClick={() => addSection('image')} className="action-btn secondary">Add Image</button>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="memberSearch">Add Members</label>
                <input
                  id="memberSearch"
                  type="text"
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search members by name"
                  className="form-control"
                />
                <div className="member-search-results">
                  {filteredMembers.slice(0, 8).map(member => (
                    <div
                      key={member._id}
                      className={`member-search-item${selectedMembers.some(m => m._id === member._id) ? ' selected' : ''}`}
                      onClick={() => handleSelectMember(member)}
                      style={{ cursor: 'pointer', opacity: selectedMembers.some(m => m._id === member._id) ? 0.5 : 1 }}
                    >
                      {member.name} {member.email && <span className="member-email">({member.email})</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group selected-members">
                <label>Selected Members</label>
                <div className="selected-members-list">
                  {selectedMembers.map(member => (
                    <div key={member._id} className="selected-member-card">
                      <span>{member.name}</span>
                      <button type="button" onClick={() => handleRemoveMember(member._id)}>&times;</button>
                    </div>
                  ))}
                  {selectedMembers.length === 0 && <span className="no-members">No members selected</span>}
                </div>
              </div>
              <button type="submit" disabled={saving || imageUploading} className="action-btn primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              {message && <div className={`message ${message.startsWith('Project group updated') ? 'success-msg' : 'error-msg'}`}>{message}</div>}
            </form>
          )}
        </div>
      </div>
    </ProtectedAdminRoute>
  );
}